import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { CommandService } from '@core/services/command.service';
import { SnackBarService } from '@core/services/snack-bar.service';
import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import { PlatformCommand } from '@shared/commands/command-base';
import { Api } from '@shared/constants/common-field.constants';
import { MagicLinkModel } from '@shared/models/platform-tool-models';
import { PlatformLinkLoadedAction } from '@shared/store/actions/particle.actions';
import { ParticleState } from '@shared/store/reducers/particle.reduer';

/**
 * Route component for `/admin-panel/user/:userName`.
 *
 * Replaces the generic `PlatformLinkCreatorComponent` for this single route to avoid an encoding
 * mismatch in `CommandService.getPlatformLinkCommand`, which compares `router.url` (percent-encoded
 * — `+` becomes `%2B`) against `activatedRoute.snapshot.url[*].path` (decoded). Usernames containing
 * `+` (e.g. `jahid.mridha+test@orbitax.com`) never match the registered template and the service
 * then throws on `command.fieldValues` of an undefined command, leaving the page stuck on the
 * splash screen.
 *
 * This component bypasses that lookup entirely: it builds `AdminPanelUserDetailToolLinkCommand`
 * directly from `activatedRoute.snapshot.params['userName']` (always decoded by Angular) and
 * renders the resulting `MagicLinkModel` exactly the way `PlatformLinkCreatorComponent` does.
 */
@Component({
  standalone: false,
  selector: 'lib-admin-panel-user-detail-link',
  template: `
    <particle-platform-tool-container *ngIf="isReady" [magicLinkModel]="magicLinkModel">
    </particle-platform-tool-container>
  `,
  styles: [`
    :host { display: flex; width: 100%; height: 100%; }
    ::ng-deep particle-platform-tool-container { display: flex; flex-grow: 1; }
  `]
})
export class AdminPanelUserDetailLinkComponent implements OnInit, OnDestroy {
  private readonly menuUrl = '/admin-panel/user/:userName';
  private ngUnsubscribe = new Subject<void>();

  magicLinkModel: MagicLinkModel;
  isReady = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private commandService: CommandService,
    private snackBarService: SnackBarService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private particleStore: Store<ParticleState>
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(params => {
        const userName = params?.['userName'];
        if (!userName) {
          return;
        }
        this.loadUserDetail(userName);
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private loadUserDetail(userName: string): void {
    this.fuseSplashScreenService.show();
    this.isReady = false;

    const command = new PlatformCommand('AdminPanelUserDetailToolLinkCommand', Api.AdminPanel);
    command.waitForResponse = true;
    command.fieldValues['MenuUrl'] = this.menuUrl;
    command.fieldValues['userName'] = userName;
    command.fieldValues['QueryParameter'] = this.router.url.split('?')[1] ?? '';

    this.commandService
      .executePlatformCommand(command)
      .pipe(take(1))
      .subscribe(
        response => {
          if (response.fieldValues['ShowErrorMessage']) {
            this.snackBarService.showError(response.fieldValues['ShowErrorMessage']);
          }
          if (response.fieldValues['RedirectUrl']) {
            this.router.navigateByUrl(response.fieldValues['RedirectUrl']);
            return;
          }
          if (response.fieldValues['ReplaceWindowUrl']) {
            window.location.href = response.fieldValues['ReplaceWindowUrl'];
            return;
          }
          if (response.fieldValues['PlatformLinkModel']) {
            this.magicLinkModel = response.fieldValues['PlatformLinkModel'];
            this.particleStore.dispatch(new PlatformLinkLoadedAction(this.magicLinkModel));
            this.magicLinkModel.fieldValues['DispatchedMagicLink'] = true;
            this.isReady = true;
            return;
          }
          if (response.fieldValues['SnackBarMessage']) {
            const duration = response.fieldValues['SnackBarMessage'].length * 80;
            this.snackBarService.showInformation(response.fieldValues['SnackBarMessage'], duration);
          }
        },
        error => {
          this.fuseSplashScreenService.hide();
          console.error('Failed AdminPanelUserDetailToolLinkCommand', error);
        }
      );
  }
}
